import { noop } from 'lodash/fp';
import { forwardRef, useRef } from 'react';
import { Disableable, handleRefSplit } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { Activable, Identified, Inlined } from '../../types';
import { TAB_DATA_ATTRIBUTES } from '../constants';
import { ButtonType } from '../../Button';
import { BadgeElement, IconStyled, TabContainerElement, TabIconContainer, TabLabelElement } from './style';

export interface TabProps extends Activable, Inlined, Identified, Disableable {
    label: React.ReactNode;
    value: string;
    icon?: IconName;
    underline?: boolean;
    notificationsCount?: number;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>, value: string) => void;
    forwardedRef?: React.Ref<HTMLButtonElement>;
    showDivider?: boolean;
    dataTestTab?: string;
    as?: React.ElementType;
    'aria-selected'?: boolean;
    tabIndex?: number;
    [TAB_DATA_ATTRIBUTES.TAB_VALUE]?: string;
    type?: ButtonType;
}

const Tab: React.FC<TabProps> = ({
    label,
    id,
    forwardedRef,
    value,
    icon,
    notificationsCount,
    onClick = noop,
    active,
    disabled,
    dataTestTab,
    'aria-selected': ariaSelected,
    tabIndex,
    [TAB_DATA_ATTRIBUTES.TAB_VALUE]: dataTabValue,
    type,
    ...other
}) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const { current: button } = buttonRef;

        if (button) {
            button.blur();
        }

        onClick(event, value);
    };

    const tabContainerProps = {
        value,
        active,
        hasIcon: !!icon,
        ref: handleRefSplit<HTMLElement | null>({ ownRef: buttonRef, forwardedRef }),
        role: 'tab',
        [TAB_DATA_ATTRIBUTES.TEST_TAB]: dataTestTab || value,
        [TAB_DATA_ATTRIBUTES.TEST_TAB_ACTIVE]: active,
        [TAB_DATA_ATTRIBUTES.TAB_ITEM_ACTIVE]: active,
        [TAB_DATA_ATTRIBUTES.TAB_VALUE]: dataTabValue,
        'aria-selected': ariaSelected,
        tabIndex,
        disabled,
        ...(type ? { type } : {}),
        ...other,
    };

    return (
        <TabContainerElement
            {...tabContainerProps}
            onClick={disabled ? undefined : handleClick}
            aria-disabled={disabled}
        >
            {icon ? (
                <TabIconContainer>
                    <IconStyled value={icon} size="small" />
                </TabIconContainer>
            ) : null}
            <TabLabelElement>{label}</TabLabelElement>
            {notificationsCount ? <BadgeElement content={notificationsCount} disabled={disabled} /> : null}
        </TabContainerElement>
    );
};

export default forwardRef<HTMLButtonElement, TabProps>((props, ref) => <Tab {...props} forwardedRef={ref} />);
